import { Box, IconButton, Typography, useTheme } from '@mui/material';
import DeleteOutlined from '@mui/icons-material/DeleteOutlined';
import { useState } from 'react';
import { Control, useFieldArray, useWatch } from 'react-hook-form';

import Flex from '@Components/Bases/Flex';
import AddButtonRounded from '@Components/AddButtonRounded';
import DeleteModal from '@Components/Modal/DeleteModal';
import { useDisclosure } from '@hooks/useDisclosure';
import { FormInput } from '@Components/Form';
import { disabledFormInputStyles } from '@Components/Form/styles';
import { IGoalsAndOperationalPlansFormValues } from '@Router/GoalAndOperationalPlans/Containers/GoalAndOperationalPlansFormProvider/types';

type GoalIndicatorsFormProps = {
  goalsOperationalPlansFormControl: Control<
    IGoalsAndOperationalPlansFormValues,
    unknown
  >;
};

const GoalIndicatorsForm = ({
  goalsOperationalPlansFormControl,
}: GoalIndicatorsFormProps) => {
  const theme = useTheme();
  const [
    openedDeleteModal,
    { open: openDeleteModal, close: closeDeleteModal },
  ] = useDisclosure();
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);

  const completedSection = useWatch({
    control: goalsOperationalPlansFormControl,
    name: 'completedSection',
  });

  const { isCompletedTh, isCompletedEn } = completedSection ?? {};

  const {
    fields: indicators,
    append,
    remove,
  } = useFieldArray({
    name: 'indicators',
  });

  const handleConfirmDelete = (selectedIndex: number | null) => {
    if (selectedIndex !== null) {
      remove(selectedIndex);
      closeDeleteModal();
      setSelectedIndex(null);
    }
  };

  return (
    <>
      {indicators.map((indicator, index) => (
        <Box
          key={indicator.id}
          border={`1px solid ${theme.palette.grey[200]}`}
          borderRadius={1}
          p={1}
          mt={2}
        >
          <Flex justifyContent="space-between" alignItems="center">
            <Typography fontWeight="bold">ตัวชี้วัดที่ {index + 1}</Typography>
            {indicators.length > 1 && (
              <IconButton
                size="small"
                edge="end"
                aria-label="delete"
                color="inherit"
                onClick={() => {
                  setSelectedIndex(index);
                  openDeleteModal();
                }}
                disabled={isCompletedTh || isCompletedEn}
              >
                <DeleteOutlined style={{ fontSize: '16px' }} />
              </IconButton>
            )}
          </Flex>
          <Flex flexDirection="column" gap={2} mt={1}>
            <FormInput
              name={`indicators.${index}.criteriaTh`}
              label="เกณฑ์การวัดผล (ไทย)"
              placeholder="กรุณาระบุเกณฑ์การวัดผล (ไทย)"
              multiline
              minRows={3}
              required
              sx={isCompletedTh ? disabledFormInputStyles : undefined}
            />
            <FormInput
              name={`indicators.${index}.criteriaEn`}
              label="เกณฑ์การวัดผล (อังกฤษ)"
              placeholder="กรุณาระบุเกณฑ์การวัดผล (อังกฤษ)"
              multiline
              minRows={3}
              required
              sx={isCompletedEn ? disabledFormInputStyles : undefined}
            />
          </Flex>
        </Box>
      ))}
      <Box mt={2}>
        <AddButtonRounded
          name="เพิ่มตัวชี้วัด"
          onClick={() => append({})}
          disabled={isCompletedTh || isCompletedEn}
        />
      </Box>
      <DeleteModal
        topic={`ตัวชี้วัดที่ ${
          selectedIndex !== null ? selectedIndex + 1 : ''
        }`}
        opened={openedDeleteModal}
        close={closeDeleteModal}
        confirm={() => handleConfirmDelete(selectedIndex)}
      />
    </>
  );
};

export default GoalIndicatorsForm;
